import { produce } from 'immer';
import { isNil, isEmpty } from 'lodash/fp';
import { DashboardTabs, PrimaryDimensions, ChartType, MEASURES } from 'app/lib/constants';
import {
    CHECK_WAREHOUSE_USAGE_TABLE_ACCESS,
    GET_QUERY_HISTORY_COLUMNS,
    RESET_WAREHOUSE_CUBE_FILTERS,
    SELECT_TAB,
    SELECT_WAREHOUSES,
    SET_WAREHOUSE_CUBE_DATE_FILTER_AND_BIN,
    SET_WAREHOUSE_CUBE_DATE_FILTER,
    SET_WAREHOUSE_CUBE_MEASURE,
    SET_WAREHOUSE_CUBE_SECONDARY_DIMENSION,
    SET_WAREHOUSE_CUBE_PRIMARY_DIMENSION,
    SET_WAREHOUSE_CUBE_USER_FILTER,
    SET_WAREHOUSE_CUBE_WAREHOUSE_FILTER,
    SET_WAREHOUSE_FILTER,
    SELECT_CHART_AREA,
    CHANGE_CHART_TYPE,
    GET_USAGE_JOB_DEFINITION
} from './types.js';

export const INITIAL_STATE = {
    warehouseCube: {
        hasAccessToWarehouseUsageTable: null,
        dateFilter: null,
        warehouseFilter: null,
        userFilter: null,
        primaryDimension: PrimaryDimensions.DATE,
        secondaryDimension: null,
        selectedMeasure: MEASURES[0],
        dateBin: null,
        chartType: ChartType.LINE_CHART,
        selectedChartArea: []
    },
    spendOverview: {
        selectedWarehouses: [],
        warehouseFilter: null
    },
    queryHistoryColumns: null,
    selectedTab: DashboardTabs.SPEND_OVERVIEW,
    tcoJob: { isLoading: false, jobDef: null },
    warehouseJob: { isLoading: false, jobDef: null },
    snowflakeHistoryIndexingJob: { isLoading: false, jobDef: null }
};

export const usageReportingReducer = produce((draft, action) => {
    switch (action.type) {
        case SELECT_WAREHOUSES: {
            const { warehouses } = action.payload;

            draft.spendOverview.selectedWarehouses = warehouses;
            draft.warehouseCube.warehouseFilter = warehouses;

            break;
        }
        case SET_WAREHOUSE_FILTER: {
            const { warehouses } = action.payload;

            if (!isNil(warehouses)) {
                draft.spendOverview.selectedWarehouses =
                    draft.spendOverview.selectedWarehouses.filter((selectedWarehouse) =>
                        warehouses.includes(selectedWarehouse)
                    );
            }

            if (isEmpty(draft.spendOverview.selectedWarehouses)) {
                draft.warehouseCube.warehouseFilter = warehouses;
            }

            draft.spendOverview.warehouseFilter = warehouses;

            break;
        }
        case CHECK_WAREHOUSE_USAGE_TABLE_ACCESS.FAILED:
            draft.warehouseCube.hasAccessToWarehouseUsageTable = false;
            break;
        case CHECK_WAREHOUSE_USAGE_TABLE_ACCESS.SUCCEEDED:
            draft.warehouseCube.hasAccessToWarehouseUsageTable = true;
            break;
        case SET_WAREHOUSE_CUBE_DATE_FILTER:
            draft.warehouseCube.dateFilter = action.payload;
            draft.warehouseCube.selectedChartArea = [];
            break;
        case SET_WAREHOUSE_CUBE_WAREHOUSE_FILTER:
            draft.warehouseCube.warehouseFilter = action.payload;
            draft.warehouseCube.selectedChartArea = [];
            break;
        case SET_WAREHOUSE_CUBE_USER_FILTER:
            draft.warehouseCube.userFilter = action.payload;
            draft.warehouseCube.selectedChartArea = [];
            break;
        case RESET_WAREHOUSE_CUBE_FILTERS:
            draft.warehouseCube.userFilter = null;
            draft.warehouseCube.warehouseFilter = null;
            draft.warehouseCube.dateFilter = null;
            draft.warehouseCube.selectedChartArea = [];
            break;
        case SET_WAREHOUSE_CUBE_SECONDARY_DIMENSION:
            draft.warehouseCube.secondaryDimension = action.payload;
            break;
        case SET_WAREHOUSE_CUBE_PRIMARY_DIMENSION: {
            draft.warehouseCube.primaryDimension = action.payload;
            draft.warehouseCube.selectedChartArea = [];
            if (draft.warehouseCube.secondaryDimension === action.payload) {
                draft.warehouseCube.secondaryDimension = null;
            }
            if (action.payload !== PrimaryDimensions.DATE) {
                draft.warehouseCube.chartType = ChartType.BAR_CHART;
            }
            break;
        }
        case SET_WAREHOUSE_CUBE_MEASURE:
            draft.warehouseCube.selectedMeasure = action.payload;
            break;
        case SET_WAREHOUSE_CUBE_DATE_FILTER_AND_BIN: {
            const { rangeFrom, rangeTo, bin } = action.payload;
            draft.warehouseCube.dateBin = bin;
            draft.warehouseCube.dateFilter = { rangeFrom, rangeTo };
            draft.warehouseCube.selectedChartArea = [];
            break;
        }
        case GET_QUERY_HISTORY_COLUMNS.SUCCEEDED:
            draft.queryHistoryColumns = action.payload.columns;
            break;
        case GET_QUERY_HISTORY_COLUMNS.FAILED:
            draft.queryHistoryColumns = null;
            break;
        case SELECT_TAB:
            draft.selectedTab = action.payload.id;
            draft.warehouseCube.selectedChartArea = [];
            break;
        case SELECT_CHART_AREA:
            draft.warehouseCube.selectedChartArea = action.payload;
            break;
        case CHANGE_CHART_TYPE:
            draft.warehouseCube.chartType = action.payload;
            draft.warehouseCube.selectedChartArea = [];
            break;
        case GET_USAGE_JOB_DEFINITION.STARTED: {
            const { key } = action.meta;
            draft[key].isLoading = true;
            draft[key].jobDef = null;
            break;
        }
        case GET_USAGE_JOB_DEFINITION.SUCCEEDED: {
            const { key } = action.meta;
            draft[key].jobDef = action.payload;
            break;
        }
        case GET_USAGE_JOB_DEFINITION.ENDED: {
            const { key } = action.meta;
            draft[key].isLoading = false;
            break;
        }
        default:
            break;
    }
}, INITIAL_STATE);
